import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Decimal, Numeric } from 'decimal.js-light';
import { i18n, reportStatistics, wxBridge } from '@tencent/vb-web-base';
import { isFusionBankApp, isWeChat, setTitle as setTitleBridge } from '@tencent/vb-web-base-core';
import { formatFloat, numberWithCommas } from '@common/lib/math';
import { modal } from '@tencent/web-vbui/src/components/t-modal/modal';
import { spin as loadingModal } from '@tencent/web-vbui/src/components/t-spin/spin';
import Contract from '@tencent/vb-web-contract';
import { ILanguageRsp } from '@tencent/vb-web-base/dist/i18n';
import { IRejectReasonInfo } from '@common/core/interface/IModel';
import { weixin } from '@tencent/vb-web-security';
import { PRODUCT_CONTRACT_PREVIEW, ProductInfoMap, ProductType } from '@common/core/help/constant';
import { verify } from '../utils';
import { navigator as navigatorApi } from '@tencent/vb-app-bridge';
import { getUrlQuery } from '@common/utils/param';
import { useProduct } from '@common/composables/useProduct';

const inApp = window.navigator.userAgent.indexOf('VB') > -1;

interface IResult {
  tag: string;
  rst: string;
  loanNo?: string;
  seqNo?: string;
  waitCheck?: 'yes' | 'no';
  tradeNo?: string;
  code?: string;
  message?: string;
  money?: string;
  moneyType?: string;
  account?: string;
  prodNo?: string;
  ccyCode?: string;
  transferMoney?: string; // 快速转账金额
  extInfo?: string;
}

export function baseMixins() {
  const router = useRouter();
  const { getT } = i18n;
  const $t = getT();
  const loadingShow = ref(false);

  function hideModal() {
    modal.hide();
  }

  async function showModal(
    content: string | string[],
    title?: string,
    cancelText?: string,
    confirmText?: string,
    richText?: string,
    theme?: string
  ) {
    const newParams = {
      title: typeof title !== 'undefined' ? title : $t('温馨提示'),
      content,
      isNeedClose: true,
      footer: true,
      isNeedCloseCancel: true,
      cancelText: typeof cancelText !== 'undefined' ? cancelText : '',
      confirmText: typeof confirmText !== 'undefined' ? confirmText : $t('确定'),
      richText,
      theme
    };
    return await modal.show(newParams);
  }

  async function showModalError(
    content: string | string[],
    code?: string | number | null | undefined,
    confirmText?: string,
  ) {
    if (code) {
      if (Array.isArray(content)) {
        content.push(`(${code})`);
      } else {
        // eslint-disable-next-line no-param-reassign
        content = [content, `(${code})`];
      }
    }
    reportStatistics({
      evtName: 'showModalError',
      extraMsg: content.toString(),
      desc: code?.toString()
    });
    const newParams = {
      title: $t('温馨提示'),
      content,
      isNeedClose: true,
      footer: true,
      isNeedCloseCancel: true,
      cancelText: '',
      confirmText: confirmText || $t('我知道了'),
    };

    return await modal.show(newParams);
  }

  async function showAssociationModal(type: 'repay' | 'detail' | 'loan') {
    const isAss = await weixin.checkAssociation('walletLoan');
    if (isAss) {
      return Promise.resolve(true);
    }
    const contentObj = {
      repay: $t('请将 WeChat Pay HK 账户关联至富融银行账户，并进行设备可信任绑定，即可还款'),
      detail: $t('请将 WeChat Pay HK 账户关联至富融银行账户，并进行设备可信任绑定，即可查询'),
      loan: $t('请将 WeChat Pay HK 账户关联至富融银行账户，并进行设备可信任绑定，即可提款'),
    };
    const continueObj = {
      repay: $t('继续还款'),
      detail: $t('继续查询'),
      loan: $t('继续提款'),
    };
    const newParams = {
      content: contentObj[type],
      isNeedClose: true,
      footer: true,
      isNeedCloseCancel: true,
      cancelText: $t('返回'),
      confirmText: $t('确认'),
    };
    const res = await modal.show(newParams);
    if (res === 'confirm') {
      await reportStatistics({
        evtName: 'confirmWalletLoanAssociation',
      });
      weixin.association('walletLoan', continueObj[type]);
      return Promise.resolve(false);
    }
    await reportStatistics({
      evtName: 'cancelWalletLoanAssociation',
    });
    return Promise.resolve(false);
  }

  function showLoading(show: boolean, title = '', timeOut?: number) {
    loadingShow.value = show;
    if (show) {
      loadingModal.show({ title });
      if (timeOut) {
        setTimeout(() => {
          loadingModal.hide();
        }, timeOut);

      }
    } else {
      loadingModal.hide();
    }
  }

  function gotoPro(key: string, viewType: 'iframe' | 'location' = 'location') {
    const contract = new Contract({
      key,
      type: 'proto',
      needTitle: false,
      // needTitle: false,
      // needBackBtn: true,
      viewType,
    });
    contract.viewProto();
  }

  function jumpToResult(info: IResult) {
    router.replace({
      path: '/result',
      query: {
        tag: info.tag,
        rst: info.rst,
        money: info.money || '',
        moneyType: info.moneyType || '',
        account: info.account || '',
        loanNo: info.loanNo || '',
        code: info.code || '',
        tradeNo: info.tradeNo || '',
        message: info.message || '',
        prodNo: info.prodNo,
        transferMoney: info.transferMoney || '',
        extInfo: info.extInfo,
      },
    });
  }

  // 跳转到首页
  function goHomePage() {
    // const activeTag = getBaseParam('activeTag');
    // navigatorApi.push({
    //   path: '/e/basic/loan.html/home',
    //   query: {
    //     activeTag,
    //   },
    // });
    if (isFusionBankApp) {
      navigatorApi.switchTab('1');
    } else {
      router.replace({
        path: '/limit',
      });
    }

    return true;
  }

  /**
   * 将千分号分割的数值还原成原始数值
   */
  function formatThousandsToRaw(val: string) {
    return val.replace(/,/g, '');
  }

  function checkMoney(value: string, minx?: number, max?: number, mode?: number) {
    if (!value) {
      return {
        code: -1,
        msg: '',
      };
    }

    if (value !== '' && isNaN(Number(value))) {
      return {
        code: -1,
        msg: $t('只能输入数字'),
      };
    }
    const numberVal = Number(value);
    if (numberVal <= 0) {
      return {
        code: -2,
        msg: $t('只能输入正数'),
      };
    }
    if (minx !== undefined && max !== undefined) {
      if (numberVal < minx) {
        return {
          code: -3,
          msg: `${$t('最小只能')}${minx}`,
        };
      }
      if (numberVal > max) {
        return {
          code: -4,
          msg: $t('请填写少于{amt}的数字', { amt: max }),
        };
      }
    }
    if (value.indexOf('.') >= 0) {
      return {
        code: -5,
        msg: `${$t('只能输入整数')}`,
      };
    }

    if (mode) {
      if (numberVal % mode !== 0) {
        return {
          code: -6,
          msg: $t('只可输入{number}的整数倍', { number: mode }),
        };
      }
    }

    return {
      code: 0,
      msg: '',
    };
  }

  async function protoPreviewRichText(pseudoRef: string, needLogin = true) {
    if (!pseudoRef) {
      return;
    }
    const contractKFSObj = new Contract({
      key: pseudoRef,
      autoSign: false,
      hideBtn: true,
      needLogin,
      needTitle: false,
    });
    await contractKFSObj.preview();
  }
  const isEng = computed(() => {
    const lang = i18n.getLang();
    return lang === 'en-GB';
  });

  function formatWithCommas(src: string | number) {
    return numberWithCommas(src);
  }

  function fixed(src: string | number, digits = 2) {
    return sum([src], digits);
  }

  function formatMoney(moneyStr: string) {
    return moneyStr ? numberWithCommas(sum([moneyStr], 2)) : moneyStr;
  }
  function formatMoneyInt(moneyStr: string) {
    return moneyStr ? numberWithCommas(moneyStr) : moneyStr;
  }

  function formatFloatV2(s: Numeric, digits: number) {
    return formatFloat(s, digits);
  }

  function setTitle(title: string) {
    setTitleBridge(title);
  }

  function setDefaultTitle() {
    const map: { [prop: string]: string } = {};
    map[ProductType.NPP] = $t('循环易借');
    map[ProductType.NPPTESLA] = $t('Tesla专享贷款');
    map[ProductType.INSTALLMENT] = $t('私人分期贷款');
    if (!isFusionBankApp) {
      setTitle('Fusion Bank');
      return;
    }
    const productInfoStore = useProduct();
    const tag = productInfoStore.productState.productTag;
    if (map[tag]) {
      setTitle(map[tag]);
    }
  }

  function getLanguage() {
    return i18n.getLang();
  }

  type IGetBaseParam = 'prodNo' | 'activeTag' | 'productTag';
  function getBaseParam(key: IGetBaseParam): string;
  function getBaseParam(): { prodNo: string; activeTag: string; productTag: string };
  function getBaseParam(key?: IGetBaseParam) {
    const defaultProdNo = isWeChat ? ProductInfoMap.DEFAULT : '';
    const { productState } = useProduct();
    const baseParam = {
      prodNo:
        getUrlQuery('prodNo') ||
        getUrlQuery('product_no') ||
        getUrlQuery('productNo') ||
        productState.proNo ||
        defaultProdNo,
      activeTag: getUrlQuery('activeTag'),
      productTag: productState.productTag || getUrlQuery('productTag'),
    };
    if (key) {
      return baseParam[key] || '';
    }
    return baseParam;
  }

  /**
   * 跳转官方下载地址
   */
  function goDownloadPage() {
    // location.href = '/h5/account/account/index.html#/download';
    location.href = '/h5/account/account/index.html#/download?source=walletLoan';
  }

  function isNumberTest(x: unknown): boolean {
    if (typeof x !== 'string') {
      return false;
    }
    if (x.length === 0) {
      return false;
    }
    const num = Number(x);
    return !isNaN(num);
  }
  function toNumber(src: Numeric): number {
    return new Decimal(src).toNumber();
  }

  function roundFix(x: Numeric, fixed: number): string {
    return new Decimal(x).toFixed(fixed);
  }

  function mod(x: Numeric, y: Numeric): Decimal {
    return new Decimal(x).mod(y);
  }

  function minus(x: Numeric, y: Numeric): Decimal {
    return new Decimal(x).minus(y);
  }
  function sum(numbers: Numeric[], fixed?: number): string {
    const sumDec = numbers.reduce((pre: Decimal, item) => pre.plus(item), new Decimal(0));

    return fixed ? new Decimal(sumDec).toFixed(fixed) : sumDec.toString();
  }

  function closeWindow() {
    if (wxBridge.wx) {
      wxBridge.wx.closeWindow();
    }
  }

  function getNotPassReason(notpassReason: IRejectReasonInfo, lang: ILanguageRsp): string {
    let reason = '';
    switch (lang) {
      case 'zh-CN':
        reason = notpassReason['zh-CN'];
        break;
      case 'zh-HK':
        reason = notpassReason['zh-HK'];
        break;
      case 'en-GB':
        reason = notpassReason['en-GB'];
        break;
    }
    return reason || '';
  }

  function checkIsTesla() {
    const productInfoStore = useProduct();
    return productInfoStore.productState.productTag === ProductType.NPPTESLA;
  }

  function checkIsInstallment() {
    const productInfoStore = useProduct();
    return productInfoStore.productState.productTag === ProductType.INSTALLMENT;
  }

  function formatRate(rate: string, dig = 2) {
    if (!rate) {
      return '';
    }
    return `${formatFloatV2(rate, dig)}%`;
  }

  async function weexVerify(secSession: string, steps: string, title: string, ccyCode: string, money: string) {
    return await verify(
      steps,
      secSession,
      {
        title,
        ccyCode,
        money,
      },
      'loan',
    );
  }

  async function previewContract(productNo: string, configContractKeys?: string, needFinger?: boolean) {
    let contractKeys = [];
    if (configContractKeys) {
      contractKeys = configContractKeys.split(',') || [];
    } else {
      contractKeys = PRODUCT_CONTRACT_PREVIEW[productNo]?.split(',') || [];
    }
    let result = true;
    for await (const k of contractKeys) {
      reportStatistics({
        evtName: `previewLoanContract_${k}`,
      });
      const instance = new Contract({
        key: k,
        autoSign: false,
        needFinger,
        containerType: 'dialog',
        needLogin: false,
        needTitle: false,
        needAgree: true,
        noClose: true,
        needCheckBox: false,
        alarmText: $t('一经按下“已阅读并同意”，我确认我已阅读、明白及同意以上文件的条款、细则及声明。'),
      });
      const previewResult = (await instance.preview()) as any;
      showLoading(false);
      if (previewResult && (previewResult.code === 0 || previewResult.code === 2)) {
        reportStatistics({
          evtName: `agreeLoanContract_${k}`,
        });
        result = true;
      } else {
        reportStatistics({
          evtName: `notAgreeLoanContract_${k}`,
        });
        result = false;
        break;
      }
    }
    return result;
  }

  function sleep(time: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, time);
    });
  }

  return {
    hideModal,
    getBaseParam,
    isEng,
    inApp,
    loadingShow,
    goHomePage,
    showLoading,
    showModal,
    showModalError,
    gotoPro,
    jumpToResult,
    formatThousandsToRaw,
    checkMoney,
    protoPreviewRichText,
    formatWithCommas,
    fixed,
    formatMoney,
    formatMoneyInt,
    formatFloatV2,
    setTitle,
    getLanguage,
    numberWithCommas,
    goDownloadPage,
    isNumberTest,
    toNumber,
    roundFix,
    mod,
    minus,
    formatRate,
    sum,
    closeWindow,
    setDefaultTitle,
    getNotPassReason,
    showAssociationModal,
    checkIsTesla,
    checkIsInstallment,
    weexVerify,
    previewContract,
    sleep,
  };
}
